import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import Header from "../Header";
import {
  pioneerPacks,
  pioneerPacksSync,
  pioneerPackUpdate,
  pioneerPackReset,
  pioneerRenewals,
  pioneerRenewalCancel,
  pioneerRenewalRetry,
} from "../../api/pioneerApi";
import { fmtDate, parseDate, rupees } from "../PioneerIptv/packRules";
import "../Combo/combo.css";
import "../PioneerIptv/pioneer.css";

const errText = (err, fallback) =>
  err?.response?.data?.error || err?.response?.data?.message || err?.message || fallback;

const fmtDT = (v) => {
  const d = parseDate(v);
  return d
    ? d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true })
    : "—";
};

const TYPES = ["A-la-carte", "Broadcaster", "DPO", "FTA"];

// ─────────────────────────────────────────────────────────────
// Packs: Pioneer's catalog, add-on prices, sync
// ─────────────────────────────────────────────────────────────
const PacksTab = () => {
  const [type, setType] = useState("A-la-carte");
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [edits, setEdits] = useState({}); // id -> price text
  const [saving, setSaving] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await pioneerPacks({ type });
      setData(d);
      setEdits({});
    } catch (err) {
      toast.error(errText(err, "Could not load the packs"));
    } finally {
      setLoading(false);
    }
  }, [type]);

  useEffect(() => { load(); }, [load]);

  const sync = async () => {
    setSyncing(true);
    try {
      const { data: d } = await pioneerPacksSync(true);
      toast.success(d.message || "Synced");
      load();
    } catch (err) {
      toast.error(errText(err, "Sync failed"));
    } finally {
      setSyncing(false);
    }
  };

  const save = async (p, patch) => {
    setSaving(p.id);
    try {
      await pioneerPackUpdate(p.id, patch);
      toast.success(`${p.name} saved`);
      setData((d) => ({ ...d, packs: d.packs.map((x) => (x.id === p.id ? { ...x, ...patch } : x)) }));
      setEdits((e) => { const n = { ...e }; delete n[p.id]; return n; });
    } catch (err) {
      toast.error(errText(err, "Could not save"));
    } finally {
      setSaving(null);
    }
  };

  const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const packs = (data?.packs || []).filter((p) => {
    if (!words.length) return true;
    const hay = [p.name, String(p.pioneer_id), ...(p.channels || [])].join(" ").toLowerCase();
    return words.every((w) => hay.includes(w));
  });
  const isAddon = type === "A-la-carte" || type === "Broadcaster";
  const priced = isAddon || type === "DPO";
  const counts = data?.counts || {};
  const gst = data?.gst_percent ?? 18;

  const reset = async (p) => {
    setSaving(p.id);
    try {
      const { data: d } = await pioneerPackReset(p.id);
      toast.success(`${p.name}: ${d.message}`);
      setData((x) => ({ ...x, packs: x.packs.map((y) => (y.id === p.id ? { ...y, price: d.price, price_manual: 0 } : y)) }));
      setEdits((e) => { const n = { ...e }; delete n[p.id]; return n; });
    } catch (err) {
      toast.error(errText(err, "Could not reset"));
    } finally {
      setSaving(null);
    }
  };

  return (
    <>
      <div className="cmb-note cmb-note--info" style={{ marginBottom: 12 }}>
        Every selling price here is saved <strong>with {gst}% GST</strong>, per 30 days. On each sync a pack's price
        becomes Pioneer's price + GST (a DPO: DPO + FTA + GST — its base price on the Assign Pioneer IPTV Plans page),
        unless you set it by hand — then it stays until you press <em>Reset</em>. DPO packs are assigned to admins /
        operators from Operators / Admins → Assign Pioneer IPTV Plans. A-la-carte channels and broadcaster
        packs are available to every operator that sells Pioneer IPTV, at the price set here. A customer can't add a
        channel that is already in their DPO or another add-on.
      </div>

      <div className="piptv-toolbar">
        <div className="piptv-chipset">
          {TYPES.map((t) => (
            <button key={t} type="button" className={`piptv-chip ${type === t ? "is-on" : ""}`} onClick={() => { setType(t); setQ(""); }}>
              {t} {counts[t] !== undefined ? `(${counts[t]})` : ""}
            </button>
          ))}
        </div>
        <div className="piptv-search">
          <span aria-hidden="true">⌕</span>
          <input type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search pack, id or channel…" />
          {q ? <button type="button" className="piptv-search__clear" onClick={() => setQ("")} aria-label="Clear search">×</button> : null}
        </div>
        <div style={{ flex: 1 }} />
        <span className="cmb-hint">Last sync: {data?.last_sync ? fmtDT(data.last_sync) : "never"}</span>
        <button type="button" className="cmb-btn cmb-btn--primary" onClick={sync} disabled={syncing}>
          {syncing ? "Syncing… (reads every pack's channels)" : "Sync from Pioneer"}
        </button>
      </div>

      {loading ? (
        <div className="cmb-empty">Loading packs…</div>
      ) : packs.length === 0 ? (
        <div className="cmb-empty">
          <strong>{data?.packs?.length ? "Nothing matches" : `No ${type} packs yet`}</strong>
          {data?.packs?.length ? "Try another search." : "Press “Sync from Pioneer” to read Pioneer's pack list and channels."}
        </div>
      ) : (
        <div className="cmb-panel cmb-table-wrap">
          <table className="cmb-table">
            <thead>
              <tr>
                <th>Pack</th>
                <th className="cmb-num">Pioneer id</th>
                <th>Channels</th>
                <th className="cmb-num">Pioneer price</th>
                <th className="cmb-num">{type === "DPO" ? "+ FTA + GST" : `+ ${gst}% GST`}</th>
                <th className="cmb-num">{priced ? "Selling price incl. GST" : ""}</th>
                {type === "DPO" ? <th className="cmb-num">Assigned to</th> : null}
                {priced ? <th>Show at recharge</th> : null}
              </tr>
            </thead>
            <tbody>
              {packs.map((p) => {
                const edit = edits[p.id];
                const dirty = edit !== undefined && String(edit) !== String(p.price ?? "");
                return (
                  <tr key={p.id} style={p.in_last_sync ? undefined : { opacity: 0.55 }}>
                    <td>
                      <strong>{p.name}</strong>
                      {!p.in_last_sync ? <div className="cmb-error-text">Not in Pioneer's latest list</div> : null}
                    </td>
                    <td className="cmb-num">{p.pioneer_id}</td>
                    <td>
                      {p.channels === null ? (
                        <span className="cmb-muted">Not read — not checked for repeats</span>
                      ) : (
                        <span title={(p.channels || []).join(", ")}>
                          {p.channel_count} {p.channel_count === 1 ? "channel" : "channels"}
                          {p.channel_count > 0 && p.channel_count <= 3 ? <span className="cmb-muted"> · {(p.channels || []).join(", ")}</span> : null}
                        </span>
                      )}
                    </td>
                    <td className="cmb-num">{p.pioneer_price !== null ? rupees(p.pioneer_price) : "—"}</td>
                    <td className="cmb-num cmb-muted">{p.auto_price !== null ? rupees(p.auto_price) : "—"}</td>
                    <td className="cmb-num">
                      {priced ? (
                        <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                          ₹
                          <input
                            className="piptv-price-input"
                            type="number"
                            min="0"
                            step="1"
                            value={edit !== undefined ? edit : p.price ?? ""}
                            onChange={(e) => setEdits((x) => ({ ...x, [p.id]: e.target.value }))}
                            aria-label={`Price of ${p.name}`}
                          />
                          <button
                            type="button"
                            className="cmb-btn cmb-btn--sm cmb-btn--primary"
                            disabled={!dirty || saving === p.id}
                            onClick={() => save(p, { price: edit === "" ? null : Number(edit), price_manual: 1 })}
                          >
                            Save
                          </button>
                          {p.price_manual ? (
                            <button
                              type="button"
                              className="cmb-btn cmb-btn--sm"
                              disabled={saving === p.id}
                              title={`Back to Pioneer price + GST (${p.auto_price !== null ? rupees(p.auto_price) : "—"}) — follows future syncs`}
                              onClick={() => reset(p)}
                            >
                              Reset
                            </button>
                          ) : (
                            <span className="cmb-badge cmb-badge--info" title="Follows Pioneer price + GST on every sync">Auto</span>
                          )}
                        </span>
                      ) : type === "FTA" ? (
                        <span className="cmb-muted">Included in every DPO price</span>
                      ) : null}
                    </td>
                    {type === "DPO" ? (
                      <td className="cmb-num">
                        {p.assigned ? `${p.assigned} account${p.assigned === 1 ? "" : "s"}` : <span className="cmb-muted">Not assigned</span>}
                      </td>
                    ) : null}
                    {priced ? (
                      <td>
                        <label style={{ display: "inline-flex", gap: 6, alignItems: "center", cursor: "pointer" }}>
                          <input
                            type="checkbox"
                            checked={!!p.is_active}
                            disabled={saving === p.id}
                            onChange={(e) => save(p, { is_active: e.target.checked ? 1 : 0 })}
                          />
                          <span className="cmb-hint">{p.is_active ? "Shown" : "Hidden"}</span>
                        </label>
                      </td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

// ─────────────────────────────────────────────────────────────
// Auto-renewals: paid 3/6/12-month recharges, month by month
// ─────────────────────────────────────────────────────────────
const FILTERS = [
  { key: "", label: "All" },
  { key: "attention", label: "Needs attention" },
  { key: "waiting", label: "Months to come" },
  { key: "done", label: "Completed" },
  { key: "cancelled", label: "Cancelled" },
];

const RenewalsTab = () => {
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await pioneerRenewals({ status, q: query, page, limit: 30 });
      setData(d);
    } catch (err) {
      toast.error(errText(err, "Could not load the renewals"));
    } finally {
      setLoading(false);
    }
  }, [status, query, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); setQuery(q.trim()); }, 350);
    return () => clearTimeout(t);
  }, [q]);

  const retry = async (m) => {
    setBusy(`m${m.id}`);
    try {
      const { data: d } = await pioneerRenewalRetry(m.id);
      (d.success ? toast.success : toast.warn)(d.message);
      load();
    } catch (err) {
      toast.error(errText(err, "Retry failed"));
    } finally {
      setBusy(null);
    }
  };

  const cancel = async (b) => {
    if (!window.confirm(`Stop the months not sent yet for ${b.device_id}? Nothing is refunded automatically.`)) return;
    setBusy(`b${b.batch}`);
    try {
      const { data: d } = await pioneerRenewalCancel(b.batch);
      toast.success(d.message);
      load();
    } catch (err) {
      toast.error(errText(err, "Could not cancel"));
    } finally {
      setBusy(null);
    }
  };

  const s = data?.summary || {};
  const pg = data?.pagination || { page: 1, total_pages: 1, total: 0 };

  return (
    <>
      <div className="piptv-stats">
        <div className="piptv-stat"><strong>{s.waiting ?? "—"}</strong><span>Paid months still to send</span></div>
        <div className="piptv-stat"><strong>{s.due_now ?? "—"}</strong><span>Due now (next hourly run)</span></div>
        <div className={`piptv-stat ${s.failed ? "piptv-stat--bad" : ""}`}><strong>{s.failed ?? "—"}</strong><span>Failed after 5 tries</span></div>
      </div>

      <div className="piptv-toolbar">
        <div className="piptv-chipset">
          {FILTERS.map((f) => (
            <button key={f.key} type="button" className={`piptv-chip ${status === f.key ? "is-on" : ""}`} onClick={() => { setStatus(f.key); setPage(1); }}>
              {f.label}
            </button>
          ))}
        </div>
        <div className="piptv-search">
          <span aria-hidden="true">⌕</span>
          <input type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Customer, mobile, STB or operator…" />
          {q ? <button type="button" className="piptv-search__clear" onClick={() => setQ("")} aria-label="Clear search">×</button> : null}
        </div>
        <div style={{ flex: 1 }} />
        <button type="button" className="cmb-btn" onClick={load} disabled={loading}>Refresh</button>
      </div>

      {loading && !data ? (
        <div className="cmb-empty">Loading…</div>
      ) : !data?.batches?.length ? (
        <div className="cmb-empty">
          <strong>Nothing here</strong>
          3, 6 and 12-month IPTV recharges appear here with each month's renewal.
        </div>
      ) : (
        <div className="cmb-panel cmb-table-wrap">
          <table className="cmb-table">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Set-top box</th>
                <th>Packs</th>
                <th>Months</th>
                <th>Paid till</th>
                <th>Next</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.batches.map((b) => {
                const isOpen = open === b.batch;
                const n = b.next_month;
                return (
                  <React.Fragment key={b.batch}>
                    <tr>
                      <td>
                        <strong>{b.customer_name || `#${b.customer_id}`}</strong>
                        <div className="cmb-muted">{b.customer_mobile} · {b.operator_name}</div>
                        <div className="cmb-muted">{b.source === "combo" ? "Combo" : "IPTV"} · {fmtDT(b.created_at)}</div>
                      </td>
                      <td className="piptv-mono">{b.device_id}</td>
                      <td style={{ maxWidth: 260 }}>
                        {b.packs.map((p) => p.name).join(", ")}
                      </td>
                      <td>
                        <div className="piptv-months-strip">
                          {b.months.map((m) => (
                            <span key={m.id} className={`piptv-mbox piptv-mbox--${m.status}`} title={`Month ${m.month_no}: ${m.status}`}>{m.month_no}</span>
                          ))}
                        </div>
                      </td>
                      <td>{fmtDate(parseDate(b.paid_till))}</td>
                      <td>
                        {!n ? (
                          <span className="cmb-badge cmb-badge--success">All sent</span>
                        ) : n.status === "failed" ? (
                          <>
                            <span className="cmb-badge cmb-badge--failed">Month {n.month_no} failed</span>
                            <div className="cmb-error-text" style={{ maxWidth: 240 }}>{n.last_error}</div>
                          </>
                        ) : (
                          <>
                            <span className="cmb-badge cmb-badge--pending">Month {n.month_no}</span>
                            <div className="cmb-muted">from {fmtDT(n.next_try_at || n.run_after)}</div>
                            {n.last_error ? <div className="cmb-error-text" style={{ maxWidth: 240 }}>{n.last_error}</div> : null}
                          </>
                        )}
                      </td>
                      <td>
                        <div className="cmb-actions">
                          {n ? (
                            <button type="button" className="cmb-btn cmb-btn--sm cmb-btn--primary" disabled={!!busy} onClick={() => retry(n)}>
                              {busy === `m${n.id}` ? "Sending…" : "Send now"}
                            </button>
                          ) : null}
                          <button type="button" className="cmb-btn cmb-btn--sm" onClick={() => setOpen(isOpen ? null : b.batch)}>
                            {isOpen ? "Hide" : "Months"}
                          </button>
                          {n ? (
                            <button type="button" className="cmb-btn cmb-btn--sm cmb-btn--danger" disabled={!!busy} onClick={() => cancel(b)}>
                              Stop
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                    {isOpen ? (
                      <tr className="piptv-detail">
                        <td colSpan={7}>
                          <table>
                            <thead>
                              <tr><th>Month</th><th>Period</th><th>Status</th><th>Tries</th><th>STB expiry seen</th><th>Note</th></tr>
                            </thead>
                            <tbody>
                              {b.months.map((m) => (
                                <tr key={m.id}>
                                  <td>{m.month_no} / {b.months_total}</td>
                                  <td>{fmtDate(parseDate(m.start_date))} – {fmtDate(parseDate(m.end_date))}</td>
                                  <td><span className={`cmb-badge cmb-badge--${m.status === "done" ? "success" : m.status === "failed" ? "failed" : m.status === "cancelled" ? "muted" : "pending"}`}>{m.status}</span></td>
                                  <td>{m.tries}</td>
                                  <td>{m.real_expiry ? fmtDT(m.real_expiry) : "—"}</td>
                                  <td className={m.last_error ? "cmb-error-text" : "cmb-muted"}>
                                    {m.last_error || (m.done_at ? `Sent ${fmtDT(m.done_at)}` : m.status === "waiting" ? `From ${fmtDT(m.next_try_at || m.run_after)}` : "")}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    ) : null}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {pg.total_pages > 1 ? (
        <div className="piptv-toolbar" style={{ justifyContent: "flex-end", marginTop: 10 }}>
          <span className="cmb-hint">Page {pg.page} of {pg.total_pages} · {pg.total} recharges</span>
          <button type="button" className="cmb-btn cmb-btn--sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</button>
          <button type="button" className="cmb-btn cmb-btn--sm" disabled={page >= pg.total_pages} onClick={() => setPage(page + 1)}>Next</button>
        </div>
      ) : null}
    </>
  );
};

const PioneerIptvAdmin = () => {
  const [tab, setTab] = useState("packs");
  return (
    <>
      <Header />
      <div className="cmb-page">
        <div className="cmb-page__head">
          <div>
            <h1 className="cmb-page__title">Pioneer IPTV</h1>
            <p className="cmb-page__sub">
              Packs and add-on prices, and the monthly renewals of 3, 6 and 12-month recharges.
            </p>
          </div>
        </div>
        <div className="piptv-page-tabs" role="tablist">
          <button type="button" role="tab" aria-selected={tab === "packs"} className={`piptv-tab ${tab === "packs" ? "is-active" : ""}`} onClick={() => setTab("packs")}>Packs &amp; add-ons</button>
          <button type="button" role="tab" aria-selected={tab === "renewals"} className={`piptv-tab ${tab === "renewals" ? "is-active" : ""}`} onClick={() => setTab("renewals")}>Auto-renewals</button>
        </div>
        {tab === "packs" ? <PacksTab /> : <RenewalsTab />}
      </div>
    </>
  );
};

export default PioneerIptvAdmin;
